import { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import type { SummaryReference, Citation, SourceDocument } from '../../types';
import { Card } from '@shared/components';
import { ScorePieChart } from '../ScorePieChart/ScorePieChart';
import { CitationConnector } from '../CitationConnector/CitationConnector';
import { LAYOUT, TIMING, GESTURES } from '@shared/config';
import './SourceDetail.css';

type SourceDetailProps = Readonly<{
  reference?: SummaryReference;
  citation?: Citation;
  source?: SourceDocument;
  citationNumber?: number;
  clickX?: number;
  clickY?: number;
  isClosing?: boolean;
  onClose: () => void;
  citationElement?: HTMLElement | null;
}>

export function SourceDetail({ reference, citation, source, citationNumber, clickX, clickY, isClosing, onClose, citationElement }: SourceDetailProps) {
  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null);

  // Callback ref to capture the panel element
  const panelRef = useCallback((node: HTMLDivElement | null) => {
    setPanelElement(node);
  }, []);

  // Measure and position panel after render
  useLayoutEffect(() => {
    if (!panelElement || !clickY) return;

    const viewportHeight = window.innerHeight;
    const panelHeight = panelElement.getBoundingClientRect().height;
    const topMargin = LAYOUT.PANEL_TOP_MARGIN;
    const bottomMargin = LAYOUT.PANEL_BOTTOM_MARGIN;

    // Position panel so citation aligns with upper quarter of panel
    const targetTop = clickY - (panelHeight * 0.25);
    const maxTop = Math.max(topMargin, viewportHeight - panelHeight - bottomMargin);
    const minTop = topMargin;
    const clampedTop = Math.max(minTop, Math.min(targetTop, maxTop));

    // Apply position directly to avoid re-render cycle
    panelElement.style.top = `${clampedTop}px`;
  }, [panelElement, clickY]);

  // Close when clicking outside (works on both desktop and mobile)
  useEffect(() => {
    // Track when the panel was mounted to ignore stale touch/click events on iOS
    const mountedAt = Date.now();
    // Detect iOS for extended debounce time
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const debounceTime = isIOS ? TIMING.IOS_DEBOUNCE_MS : TIMING.DEFAULT_DEBOUNCE_MS;

    const handleClickOutside = (e: MouseEvent) => {
      // On iOS, synthetic click events from the original tap can arrive late.
      // Ignore any click events that occur within debounce window of panel mount.
      if (Date.now() - mountedAt < debounceTime) return;

      const target = e.target as HTMLElement;

      // Don't close if clicking on the panel itself
      if (panelElement && panelElement.contains(target)) return;

      // Don't close if clicking on a citation button
      if (target.closest('.cited-content__citation')) return;

      // Don't close if clicking on the close gap button (it has its own handler)
      if (target.closest('.source-detail-container__close-gap')) return;

      onClose();
    };

    // Delay adding the listener to prevent immediate close from the event that opened it
    // Use longer delay on iOS due to touch event timing quirks
    const listenerDelay = isIOS ? TIMING.IOS_LISTENER_DELAY_MS : TIMING.DEFAULT_LISTENER_DELAY_MS;
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, listenerDelay);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [panelElement, onClose]);

  // Lock body scroll when source detail is open (mobile only)
  useEffect(() => {
    const isMobile = window.innerWidth < LAYOUT.MOBILE_BREAKPOINT;
    if (!isMobile) return;

    // Store current scroll position
    const scrollY = window.scrollY;

    // Lock scroll on both html and body
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Handle Escape key to close dialog
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Swipe to close on mobile
  useEffect(() => {
    if (!panelElement) return;

    const isLeftSide = clickX !== undefined && clickX < window.innerWidth / 2;
    const swipeThreshold = GESTURES.SWIPE_THRESHOLD_PX;
    let startX: number | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (startX === null) return;

      const endX = e.changedTouches[0].clientX;
      const deltaX = endX - startX;

      // Swipe left to close when panel is on left
      // Swipe right to close when panel is on right
      if (isLeftSide && deltaX < -swipeThreshold) {
        onClose();
      } else if (!isLeftSide && deltaX > swipeThreshold) {
        onClose();
      }

      startX = null;
    };

    panelElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    panelElement.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      panelElement.removeEventListener('touchstart', handleTouchStart);
      panelElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [panelElement, clickX, onClose]);

  // Use margin note layout for citation-based sources (new format)
  const useMarginLayout = !!(citation && source);

  // Determine if panel should be on left or right based on citation position
  const isLeftSide = clickX !== undefined && clickX < window.innerWidth / 2;
  const sideClass = isLeftSide ? 'source-detail--left' : 'source-detail--right';

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
      timeZoneName: 'short'
    });
  };

  // Support both old reference format and new citation format
  if (citation && source) {
    // New citation-based format with margin layout
    return (
      <>
        {/* SVG connector line from citation to panel */}
        <CitationConnector
          citationElement={citationElement ?? null}
          panelElement={panelElement}
          isClosing={isClosing}
          onOutOfView={onClose}
          panelOnLeft={isLeftSide}
        />

        <div
          className={`source-detail-container ${useMarginLayout ? 'source-detail-container--margin' : ''} ${isClosing ? 'source-detail-container--closing' : ''} ${isLeftSide ? 'source-detail-container--left' : 'source-detail-container--right'}`}
          role="complementary"
          aria-labelledby="source-detail-title"
        >
          {/* Clickable backdrop for mobile close */}
          <button
            className="source-detail-container__close-gap"
            onClick={onClose}
            onTouchEnd={(e) => {
              e.preventDefault();
              onClose();
            }}
            aria-label="Close source detail"
            type="button"
          />

          <div
            ref={panelRef}
            className={`source-detail source-detail--margin ${sideClass} ${isClosing ? (isLeftSide ? 'animate-slide-out-left' : 'animate-slide-out-right') : (isLeftSide ? 'animate-slide-in-left' : 'animate-slide-in-right')}`}
          >
          <Card variant="editorial" padding="md">
            <div className="source-detail__content">
              <div className="source-detail__header">
                <button
                  className="source-detail__close"
                  onClick={onClose}
                  aria-label="Close source detail"
                  type="button"
                >
                  ×
                </button>

                <div className="source-detail__source-info">
                  <h3 id="source-detail-title" className="source-detail__title font-headline">
                    [{citationNumber}] {source.sourceName}
                  </h3>
                </div>
              </div>

              <div className="source-detail__body">
                <div className="source-detail__article">
                  <h4 className="source-detail__newspaper-title">
                    <span className="source-detail__newspaper-title-text">
                      {source.title}
                    </span>
                  </h4>

                  {source.publishedAt && (
                    <p className="source-detail__date">{formatDate(source.publishedAt)}</p>
                  )}

                  {citation.citedText && (
                    <div className="source-detail__quote-block">
                      <div className="source-detail__quote-mark">&ldquo;</div>
                      <p className="source-detail__quoted-text">
                        {citation.citedText}<span className="source-detail__quote-mark-end">&rdquo;</span>
                      </p>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="source-detail__continue-link"
                      >
                        continue reading →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
          </div>
        </div>
      </>
    );
  }

  // Legacy reference format (fallback for backwards compatibility)
  if (reference) {
    const { source: refSource, index } = reference;
    return (
      <dialog
        className="source-detail-overlay"
        aria-labelledby="source-detail-title"
        open={true}
      >
        <div
          className="source-detail-overlay__wrapper"
          onClick={handleBackdropClick}
          role="presentation"
        >
          <div className="source-detail animate-scale-in">
          <Card variant="editorial" padding="xl">
            <div className="source-detail__content">
              <div className="source-detail__header">
                <button
                  className="source-detail__close"
                  onClick={onClose}
                  aria-label="Close source detail"
                >
                  ×
                </button>

                <div className="source-detail__source-info">
                  <h3 id="source-detail-title" className="source-detail__title font-headline">
                    [{index}] {refSource.name}
                  </h3>
                </div>
              </div>

              <div className="source-detail__body">
                <div className="source-detail__article">
                  <h4 className="source-detail__newspaper-title">
                    <span className="source-detail__newspaper-title-text">
                      {refSource.articleTitle}
                    </span>
                  </h4>

                  <div className="source-detail__metadata">
                    <div className="source-detail__published-date">
                      {formatDate(refSource.publishedAt)}
                    </div>
                    <div className="source-detail__actions">
                      <a
                        href={refSource.articleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="source-detail__read-link"
                      >
                        Read Full Article →
                      </a>
                    </div>
                  </div>
                </div>

                {reference.interestScore && (
                  <div className="source-detail__scoring">
                    <ScorePieChart interestScore={reference.interestScore} />
                  </div>
                )}
              </div>
            </div>
          </Card>
          </div>
        </div>
      </dialog>
    );
  }

  return null;
}